import { AlertCircle } from "lucide-react";
import { forwardRef, type InputHTMLAttributes, useId } from "react";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
	label?: string;
	error?: string;
	hint?: string;
	icon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
	({ label, error, hint, icon, id, className = "", ...props }, ref) => {
		const generatedId = useId();
		const inputId = id || generatedId;

		return (
			<div className="space-y-1.5">
				{label && (
					<label
						htmlFor={inputId}
						className="block font-medium text-sm text-text-secondary"
					>
						{label}
					</label>
				)}

				<div className="relative">
					{icon && (
						<div className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 text-text-tertiary">
							{icon}
						</div>
					)}

					<input
						ref={ref}
						id={inputId}
						className={`w-full rounded-md border bg-bg-secondary font-sans text-sm text-text-primary transition-all duration-150 placeholder:text-text-tertiary focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50 disabled:cursor-not-allowed${icon ? "pl-10" : "px-3"}
							${error ? "border-bug focus:border-bug focus:ring-bug/50" : "border-border hover:border-border-hover"}py-2.5 pr-3${className}
						`}
						{...props}
					/>
				</div>

				{error ? (
					<p className="flex items-center gap-1 text-bug text-xs">
						<AlertCircle className="h-3.5 w-3.5" />
						{error}
					</p>
				) : hint ? (
					<p className="text-text-tertiary text-xs">{hint}</p>
				) : null}
			</div>
		);
	},
);

Input.displayName = "Input";
